import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, MessageSquare, CheckCircle, Loader2 } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (priority !== 'all') params.append('priority', priority);
      
      const response = await axios.get(`${API_URL}/api/tickets?${params}`);
      setTickets(response.data.tickets || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load tickets');
      setTickets([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status, priority]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setReplying(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${selectedTicket.id}/reply`, {
        message: replyText,
        by: 'Admin'
      });
      toast.success('Reply sent successfully');
      setReplyText('');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleClose = async (ticket) => {
    try {
      await axios.put(`${API_URL}/api/tickets/${ticket.id}`, { status: 'closed' });
      toast.success('Ticket closed successfully');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const columns = [
    {
      key: 'ticket',
      header: 'Ticket',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-orange-500/20 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <p className="font-medium text-white">{row.subject}</p>
            <p className="text-xs text-slate-500">{row.user_name}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <StatusBadge status={row.priority} type="priority" />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => <span className="text-slate-400 text-sm">{formatDateTime(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`ticket-actions-${row.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
            <DropdownMenuItem 
              onClick={() => setSelectedTicket(row)}
              className="text-slate-300 focus:bg-slate-800 focus:text-white"
            >
              <Eye className="w-4 h-4 mr-2" />
              View & Reply
            </DropdownMenuItem>
            {row.status !== 'closed' && (
              <DropdownMenuItem 
                onClick={() => handleClose(row)}
                className="text-slate-300 focus:bg-slate-800 focus:text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Close Ticket
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="tickets-page">
      <PageHeader
        title="Support Tickets"
        description="Manage customer support requests"
      />

      <SearchFilter
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by subject or user..."
        filters={[
          {
            key: 'status',
            placeholder: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: 'All Status' },
              { value: 'open', label: 'Open' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'closed', label: 'Closed' },
            ],
          },
          {
            key: 'priority',
            placeholder: 'Priority',
            value: priority,
            onChange: setPriority,
            options: [
              { value: 'all', label: 'All Priority' },
              { value: 'urgent', label: 'Urgent' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ],
          },
        ]}
        onReset={() => {
          setSearch('');
          setStatus('all');
          setPriority('all');
        }}
      />

      <DataTable
        columns={columns}
        data={tickets}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No tickets found"
      />

      {/* Ticket Detail Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedTicket?.subject}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Ticket Info */}
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-slate-500">From: </span>
                <span className="text-slate-300">{selectedTicket?.user_name}</span>
              </div>
              <StatusBadge status={selectedTicket?.priority} type="priority" />
              <StatusBadge status={selectedTicket?.status} />
            </div>

            {/* Message */}
            <div className="bg-slate-800 rounded-sm p-4">
              <p className="text-slate-300 text-sm">{selectedTicket?.message}</p>
              <p className="text-xs text-slate-500 mt-2">
                {formatDateTime(selectedTicket?.created_at)}
              </p>
            </div>

            {/* Replies */}
            {selectedTicket?.replies?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300">Replies</Label>
                {selectedTicket.replies.map((reply, index) => (
                  <div key={index} className="bg-slate-800/50 rounded-sm p-3 border-l-2 border-green-500">
                    <p className="text-slate-300 text-sm">{reply.message}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {reply.by} - {formatDateTime(reply.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply */}
            {selectedTicket?.status !== 'closed' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Reply</Label>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  className="bg-slate-800 border-slate-700 min-h-[100px]"
                  data-testid="ticket-reply-input"
                />
                <div className="flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedTicket(null)}
                    className="text-slate-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={replying}
                    className="bg-green-500 hover:bg-green-600 text-slate-900"
                    data-testid="send-reply-btn"
                  >
                    {replying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Send Reply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
